import { Emulator } from "./emulator.js";
import { createWorkbenchMenuBar } from "./workbench-menu-bar.js";
import type { WorkbenchMenu, WorkbenchMenuBarController } from "./workbench-menu-types.js";
import { buttonMenuItem, menuSeparator, selectOptionMenuItem } from "./workbench-menu-utils.js";

const CHARACTER_ROM_SIZE = 0x1000;
const GLYPH_DIMENSION = 8;
const GLYPH_STRIDE = 8;
const GLYPHS_PER_BLOCK = 128;
const BYTES_PER_BLOCK = GLYPHS_PER_BLOCK * GLYPH_STRIDE;
const ATLAS_COLUMNS = 16;
const ATLAS_ROWS = 8;
const ATLAS_CELL_SIZE = 24;
const ATLAS_GLYPH_SIZE = 16;
const ATLAS_GLYPH_OFFSET = (ATLAS_CELL_SIZE - ATLAS_GLYPH_SIZE) / 2;
const EDITOR_CELL_SIZE = 32;
const TEXT_BIT7_INTERNAL_MASK = 0x20;

export type CharsetGlyphCount = 128 | 256 | 512;
type CharsetSourceKind = "rom" | "active" | "blank" | "file" | "handoff";
type ActiveCharsetBacking = "rom" | "ram";
type AtlasSlotActionKind = "copy" | "move" | "swap";

export type CharacterDraftTransfer = {
  activeBlock: number;
  bytes: Uint8Array;
  glyphCount: CharsetGlyphCount;
  sourceLabel: string;
};

type CharsetDraft = {
  glyphCount: CharsetGlyphCount;
  bytes: Uint8Array;
  sourceKind: CharsetSourceKind;
  sourceLabel: string;
  dirty: boolean;
  selectedGlyph: number;
  activeBlock: number;
};

type ActiveCharsetTarget = {
  backing: ActiveCharsetBacking;
  bank: number;
  bankBase: number;
  charOffset: number;
  glyphCount: 128 | 256;
  baseAddress: number;
  sourceLabel: string;
};

type AtlasSlotAction = {
  kind: AtlasSlotActionKind;
  sourceGlyph: number;
};

type AtlasSlotUndo = {
  activeBlock: number;
  bytes: Uint8Array;
  dirty: boolean;
  kind: AtlasSlotActionKind;
  selectedGlyph: number;
  sourceGlyph: number;
  targetGlyph: number;
};

export type CharacterEditorElements = {
  atlasCanvas: HTMLCanvasElement;
  blockMeta: HTMLElement;
  blockSelect: HTMLSelectElement;
  cancelSlotActionTrigger: HTMLButtonElement;
  charsetStatus: HTMLParagraphElement;
  clearTrigger: HTMLButtonElement;
  copySlotTrigger: HTMLButtonElement;
  dirtyIndicator: HTMLElement;
  createBlankTrigger: HTMLButtonElement;
  exportTrigger: HTMLButtonElement;
  glyphByteOutput: HTMLTextAreaElement;
  glyphEditorCanvas: HTMLCanvasElement;
  goToInput: HTMLInputElement;
  goToTrigger: HTMLButtonElement;
  importFileInput: HTMLInputElement;
  importStartBlock: HTMLSelectElement;
  importTrigger: HTMLButtonElement;
  invertTrigger: HTMLButtonElement;
  moveSlotTrigger: HTMLButtonElement;
  newBlankSize: HTMLSelectElement;
  readActiveTrigger: HTMLButtonElement;
  readRomTrigger: HTMLButtonElement;
  selectedIndex: HTMLElement;
  selectedPosition: HTMLElement;
  shiftDownTrigger: HTMLButtonElement;
  shiftLeftTrigger: HTMLButtonElement;
  shiftRightTrigger: HTMLButtonElement;
  shiftUpTrigger: HTMLButtonElement;
  sizeMeta: HTMLElement;
  sourceMeta: HTMLElement;
  sourceSummary: HTMLParagraphElement;
  slotActionSummary: HTMLParagraphElement;
  swapSlotTrigger: HTMLButtonElement;
  undoSlotActionTrigger: HTMLButtonElement;
  writeBlockTrigger: HTMLButtonElement;
  writeDraftTrigger: HTMLButtonElement;
  writeGlyphTrigger: HTMLButtonElement;
  writeHint: HTMLParagraphElement;
};

export type CharacterEditorController = {
  exportDraftForTileset(): CharacterDraftTransfer | null;
  importExternalDraft(draft: CharacterDraftTransfer): boolean;
  refresh(): void;
};

function formatHexByte(value: number): string {
  return (value & 0xff).toString(16).toUpperCase().padStart(2, "0");
}

function formatHexWord(value: number): string {
  return (value & 0xffff).toString(16).toUpperCase().padStart(4, "0");
}

function formatHexBytePrefixed(value: number): string {
  return `0x${formatHexByte(value)}`;
}

function formatHexWordPrefixed(value: number): string {
  return `0x${formatHexWord(value)}`;
}

function formatGlyphIndex(value: number): string {
  return `0x${value.toString(16).toUpperCase().padStart(3, "0")}`;
}

function formatAsmByte(value: number): string {
  return `$${formatHexByte(value)}`;
}

function atlasSlotActionLabel(kind: AtlasSlotActionKind): string {
  if (kind === "copy") {
    return "Copy";
  }
  if (kind === "move") {
    return "Move";
  }
  return "Swap";
}

function cloneBytes(data: Uint8Array): Uint8Array {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy;
}

function blockCount(glyphCount: CharsetGlyphCount): number {
  return glyphCount / GLYPHS_PER_BLOCK;
}

function glyphCountFromByteLength(byteLength: number): CharsetGlyphCount | null {
  if (byteLength === 1024) {
    return 128;
  }
  if (byteLength === 2048) {
    return 256;
  }
  if (byteLength === 4096) {
    return 512;
  }
  return null;
}

function createDraft(
  glyphCount: CharsetGlyphCount,
  bytes: Uint8Array,
  sourceKind: CharsetSourceKind,
  sourceLabel: string
): CharsetDraft {
  return {
    glyphCount,
    bytes: cloneBytes(bytes),
    sourceKind,
    sourceLabel,
    dirty: false,
    selectedGlyph: 0,
    activeBlock: 0
  };
}

function blockRangeLabel(block: number): string {
  const start = block * GLYPHS_PER_BLOCK;
  const end = start + GLYPHS_PER_BLOCK - 1;
  return `${formatGlyphIndex(start)}-${formatGlyphIndex(end)}`;
}

function parseCharsetIndex(raw: string): number {
  const text = raw.trim();
  if (text === "") {
    throw new Error("glyph index is required");
  }

  let value: number;
  if (text.startsWith("$")) {
    value = Number.parseInt(text.slice(1), 16);
  } else if (text.startsWith("0x") || text.startsWith("0X")) {
    value = Number.parseInt(text.slice(2), 16);
  } else {
    value = Number(text);
  }

  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) {
    throw new Error("glyph index must be a non-negative integer");
  }
  return value;
}

function glyphOffset(glyphIndex: number): number {
  return glyphIndex * GLYPH_STRIDE;
}

function readGlyphRowByte(draft: CharsetDraft, glyphIndex: number, row: number): number {
  return draft.bytes[glyphOffset(glyphIndex) + row] ?? 0;
}

function writeGlyphRowByte(draft: CharsetDraft, glyphIndex: number, row: number, value: number): void {
  draft.bytes[glyphOffset(glyphIndex) + row] = value & 0xff;
}

function getGlyphPixel(draft: CharsetDraft, glyphIndex: number, row: number, col: number): boolean {
  return (readGlyphRowByte(draft, glyphIndex, row) & (0x80 >> col)) !== 0;
}

function setGlyphPixel(draft: CharsetDraft, glyphIndex: number, row: number, col: number, on: boolean): void {
  const mask = 0x80 >> col;
  const current = readGlyphRowByte(draft, glyphIndex, row);
  writeGlyphRowByte(draft, glyphIndex, row, on ? current | mask : current & ~mask);
}

function clearGlyph(draft: CharsetDraft, glyphIndex: number): void {
  for (let row = 0; row < GLYPH_STRIDE; row += 1) {
    writeGlyphRowByte(draft, glyphIndex, row, 0);
  }
}

function invertGlyph(draft: CharsetDraft, glyphIndex: number): void {
  for (let row = 0; row < GLYPH_STRIDE; row += 1) {
    writeGlyphRowByte(draft, glyphIndex, row, ~readGlyphRowByte(draft, glyphIndex, row));
  }
}

function shiftGlyphLeft(draft: CharsetDraft, glyphIndex: number): void {
  for (let row = 0; row < GLYPH_STRIDE; row += 1) {
    writeGlyphRowByte(draft, glyphIndex, row, (readGlyphRowByte(draft, glyphIndex, row) << 1) & 0xff);
  }
}

function shiftGlyphRight(draft: CharsetDraft, glyphIndex: number): void {
  for (let row = 0; row < GLYPH_STRIDE; row += 1) {
    writeGlyphRowByte(draft, glyphIndex, row, readGlyphRowByte(draft, glyphIndex, row) >> 1);
  }
}

function shiftGlyphUp(draft: CharsetDraft, glyphIndex: number): void {
  for (let row = 0; row < GLYPH_STRIDE - 1; row += 1) {
    writeGlyphRowByte(draft, glyphIndex, row, readGlyphRowByte(draft, glyphIndex, row + 1));
  }
  writeGlyphRowByte(draft, glyphIndex, GLYPH_STRIDE - 1, 0);
}

function shiftGlyphDown(draft: CharsetDraft, glyphIndex: number): void {
  for (let row = GLYPH_STRIDE - 1; row > 0; row -= 1) {
    writeGlyphRowByte(draft, glyphIndex, row, readGlyphRowByte(draft, glyphIndex, row - 1));
  }
  writeGlyphRowByte(draft, glyphIndex, 0, 0);
}

function copyGlyphSlot(draft: CharsetDraft, sourceGlyph: number, targetGlyph: number): void {
  const sourceOffset = glyphOffset(sourceGlyph);
  const targetOffset = glyphOffset(targetGlyph);
  draft.bytes.set(draft.bytes.slice(sourceOffset, sourceOffset + GLYPH_STRIDE), targetOffset);
}

function moveGlyphSlot(draft: CharsetDraft, sourceGlyph: number, targetGlyph: number): void {
  copyGlyphSlot(draft, sourceGlyph, targetGlyph);
  clearGlyph(draft, sourceGlyph);
}

function swapGlyphSlots(draft: CharsetDraft, firstGlyph: number, secondGlyph: number): void {
  const firstOffset = glyphOffset(firstGlyph);
  const secondOffset = glyphOffset(secondGlyph);
  const firstBytes = draft.bytes.slice(firstOffset, firstOffset + GLYPH_STRIDE);
  const secondBytes = draft.bytes.slice(secondOffset, secondOffset + GLYPH_STRIDE);
  draft.bytes.set(secondBytes, firstOffset);
  draft.bytes.set(firstBytes, secondOffset);
}

function bankBaseForVideoCtrl(videoCtrl: number): number {
  return (videoCtrl & 0x03) << 14;
}

function readActiveCharsetTarget(emulator: Emulator): ActiveCharsetTarget {
  const voc = emulator.getVocState();
  const ioc = emulator.getIocState();
  const bank = ioc.videoCtrl & 0x03;
  const bankBase = bankBaseForVideoCtrl(ioc.videoCtrl);
  const charOffset = (voc.memPtr >> 2) & 0x03;
  const glyphCount: 128 | 256 = (voc.ctrl2 & TEXT_BIT7_INTERNAL_MASK) !== 0 ? 128 : 256;

  if (glyphCount === 256 && charOffset === 3) {
    throw new Error(
      "Active 256-glyph charset uses reserved CHAR_OFF=3; switch CHAR_OFF or enable TEXT_BIT7_INTERNAL before reading or writing the live charset."
    );
  }

  const baseAddress = bankBase + 0x1000 + (charOffset << 10);
  const backing: ActiveCharsetBacking = bank === 0 || bank === 2 ? "rom" : "ram";
  const sourceLabel =
    backing === "rom"
      ? `Active ${glyphCount}-glyph charset from character ROM (bank ${bank}, CHAR_OFF=${charOffset})`
      : `Active ${glyphCount}-glyph charset from RAM (bank ${bank}, base ${formatHexWordPrefixed(baseAddress)})`;

  return {
    backing,
    bank,
    bankBase,
    charOffset,
    glyphCount,
    baseAddress,
    sourceLabel
  };
}

function buildGlyphByteOutput(draft: CharsetDraft | null): string {
  if (draft === null) {
    return "";
  }

  const lines: string[] = [];
  for (let row = 0; row < GLYPH_STRIDE; row += 1) {
    lines.push(`.byte ${formatAsmByte(readGlyphRowByte(draft, draft.selectedGlyph, row))} ; row ${row}`);
  }
  return lines.join("\n");
}

function downloadBinary(bytes: Uint8Array, fileName: string): void {
  const blobBytes = new Uint8Array(bytes.byteLength);
  blobBytes.set(bytes);
  const blob = new Blob([blobBytes.buffer], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}

function setSelectOptions(
  select: HTMLSelectElement,
  options: Array<{ value: string; label: string }>,
  selectedValue: string
): void {
  const current = select.value;
  if (
    select.options.length === options.length &&
    options.every((option, index) => select.options[index]?.value === option.value && select.options[index]?.text === option.label)
  ) {
    select.value = options.some((option) => option.value === selectedValue)
      ? selectedValue
      : options.some((option) => option.value === current)
        ? current
        : options[0]?.value ?? "";
    return;
  }

  select.replaceChildren(
    ...options.map((option) => {
      const node = document.createElement("option");
      node.value = option.value;
      node.textContent = option.label;
      return node;
    })
  );
  select.value = options.some((option) => option.value === selectedValue) ? selectedValue : options[0]?.value ?? "";
}

function canvasPoint(event: MouseEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
  const bounds = canvas.getBoundingClientRect();
  return {
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top
  };
}

export function setupCharacterEditor(
  emulator: Emulator,
  elements: CharacterEditorElements
): CharacterEditorController {
  const menuHost = document.getElementById("characters-menu-bar");
  if (!(menuHost instanceof HTMLDivElement)) {
    throw new Error("missing character menu bar host");
  }
  let draft: CharsetDraft | null = null;
  let atlasSlotAction: AtlasSlotAction | null = null;
  let atlasSlotUndo: AtlasSlotUndo | null = null;
  let menuBar: WorkbenchMenuBarController | null = null;

  const setStatus = (message: string): void => {
    elements.charsetStatus.textContent = message;
  };

  const refreshMenuBar = (): void => {
    menuBar?.refresh();
  };

  const draftOrThrow = (): CharsetDraft => {
    if (draft === null) {
      throw new Error("Load or create a charset draft first.");
    }
    return draft;
  };

  const clearAtlasSlotAction = (): void => {
    atlasSlotAction = null;
  };

  const clearAtlasSlotUndo = (): void => {
    atlasSlotUndo = null;
  };

  const confirmDiscardDirtyDraft = (action: string): boolean => {
    if (draft === null || !draft.dirty) {
      return true;
    }
    return window.confirm(`Discard unsaved charset edits before ${action}?`);
  };

  const renderAtlas = (): void => {
    const context = elements.atlasCanvas.getContext("2d");
    if (context === null) {
      return;
    }

    context.clearRect(0, 0, elements.atlasCanvas.width, elements.atlasCanvas.height);
    context.imageSmoothingEnabled = false;

    if (draft === null) {
      context.fillStyle = "rgba(229, 239, 232, 0.72)";
      context.font = "14px Iosevka, monospace";
      context.fillText("Load or create a draft to render the atlas.", 16, 28);
      return;
    }

    const blockStart = draft.activeBlock * GLYPHS_PER_BLOCK;
    const pixelSize = ATLAS_GLYPH_SIZE / GLYPH_DIMENSION;
    const sourceGlyph = atlasSlotAction?.sourceGlyph ?? null;

    for (let row = 0; row < ATLAS_ROWS; row += 1) {
      for (let col = 0; col < ATLAS_COLUMNS; col += 1) {
        const glyphIndex = blockStart + row * ATLAS_COLUMNS + col;
        const cellX = col * ATLAS_CELL_SIZE;
        const cellY = row * ATLAS_CELL_SIZE;
        const selected = glyphIndex === draft.selectedGlyph;
        const glyphX = cellX + ATLAS_GLYPH_OFFSET;
        const glyphY = cellY + ATLAS_GLYPH_OFFSET;

        context.fillStyle = (row + col) % 2 === 0 ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.07)";
        context.fillRect(cellX, cellY, ATLAS_CELL_SIZE, ATLAS_CELL_SIZE);

        for (let glyphRow = 0; glyphRow < GLYPH_DIMENSION; glyphRow += 1) {
          for (let glyphCol = 0; glyphCol < GLYPH_DIMENSION; glyphCol += 1) {
            context.fillStyle = getGlyphPixel(draft, glyphIndex, glyphRow, glyphCol)
              ? selected
                ? "#F2B84B"
                : "#E5EFE8"
              : "rgba(10, 18, 24, 0.82)";
            context.fillRect(
              glyphX + glyphCol * pixelSize,
              glyphY + glyphRow * pixelSize,
              pixelSize,
              pixelSize
            );
          }
        }

        context.strokeStyle = selected ? "rgba(242, 184, 75, 0.9)" : "rgba(89, 191, 186, 0.22)";
        context.lineWidth = selected ? 2 : 1;
        context.strokeRect(cellX + 0.5, cellY + 0.5, ATLAS_CELL_SIZE - 1, ATLAS_CELL_SIZE - 1);

        if (sourceGlyph === glyphIndex) {
          context.save();
          context.setLineDash([4, 3]);
          context.strokeStyle = "rgba(99, 212, 200, 0.95)";
          context.lineWidth = 2;
          context.strokeRect(cellX + 3.5, cellY + 3.5, ATLAS_CELL_SIZE - 7, ATLAS_CELL_SIZE - 7);
          context.restore();
        }
      }
    }
  };

  const renderEditor = (): void => {
    const context = elements.glyphEditorCanvas.getContext("2d");
    if (context === null) {
      return;
    }

    context.clearRect(0, 0, elements.glyphEditorCanvas.width, elements.glyphEditorCanvas.height);
    context.imageSmoothingEnabled = false;

    if (draft === null) {
      context.fillStyle = "rgba(229, 239, 232, 0.72)";
      context.font = "14px Iosevka, monospace";
      context.fillText("Select a glyph to edit.", 16, 28);
      return;
    }

    for (let row = 0; row < GLYPH_DIMENSION; row += 1) {
      for (let col = 0; col < GLYPH_DIMENSION; col += 1) {
        const x = col * EDITOR_CELL_SIZE;
        const y = row * EDITOR_CELL_SIZE;
        context.fillStyle = getGlyphPixel(draft, draft.selectedGlyph, row, col)
          ? "#F2B84B"
          : (row + col) % 2 === 0
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(255, 255, 255, 0.09)";
        context.fillRect(x, y, EDITOR_CELL_SIZE, EDITOR_CELL_SIZE);
        context.strokeStyle = "rgba(8, 18, 25, 0.78)";
        context.lineWidth = 1;
        context.strokeRect(x + 0.5, y + 0.5, EDITOR_CELL_SIZE - 1, EDITOR_CELL_SIZE - 1);
      }
    }
  };

  const renderBlockOptions = (): void => {
    const draftBlocks = draft === null ? 1 : blockCount(draft.glyphCount);
    setSelectOptions(
      elements.blockSelect,
      Array.from({ length: draftBlocks }, (_, index) => ({
        value: index.toString(),
        label: blockRangeLabel(index)
      })),
      draft === null ? "0" : draft.activeBlock.toString()
    );
    elements.blockSelect.disabled = draft === null;
  };

  const renderImportStartOptions = (): void => {
    const draftBlocks = draft === null ? 1 : blockCount(draft.glyphCount);
    setSelectOptions(
      elements.importStartBlock,
      Array.from({ length: draftBlocks }, (_, index) => ({
        value: index.toString(),
        label: `Block ${index} (${blockRangeLabel(index)})`
      })),
      draft === null ? "0" : draft.activeBlock.toString()
    );
  };

  const renderMetadata = (): void => {
    if (draft === null) {
      elements.sourceMeta.textContent = "-";
      elements.sizeMeta.textContent = "-";
      elements.blockMeta.textContent = "-";
      elements.selectedIndex.textContent = "-";
      elements.selectedPosition.textContent = "-";
      elements.glyphByteOutput.value = "";
      elements.dirtyIndicator.textContent = "No Draft";
      elements.dirtyIndicator.classList.remove("is-dirty");
      elements.goToInput.disabled = true;
      elements.goToTrigger.disabled = true;
      elements.exportTrigger.disabled = true;
      elements.copySlotTrigger.disabled = true;
      elements.moveSlotTrigger.disabled = true;
      elements.swapSlotTrigger.disabled = true;
      elements.cancelSlotActionTrigger.disabled = true;
      elements.undoSlotActionTrigger.disabled = true;
      elements.clearTrigger.disabled = true;
      elements.invertTrigger.disabled = true;
      elements.shiftLeftTrigger.disabled = true;
      elements.shiftRightTrigger.disabled = true;
      elements.shiftUpTrigger.disabled = true;
      elements.shiftDownTrigger.disabled = true;
      elements.copySlotTrigger.classList.remove("is-active");
      elements.moveSlotTrigger.classList.remove("is-active");
      elements.swapSlotTrigger.classList.remove("is-active");
      elements.slotActionSummary.textContent = "Load or create a draft to use atlas slot actions.";
      return;
    }

    const glyphInBlock = draft.selectedGlyph - draft.activeBlock * GLYPHS_PER_BLOCK;
    elements.sourceMeta.textContent = draft.sourceLabel;
    elements.sizeMeta.textContent = `${draft.glyphCount} glyphs / ${draft.bytes.length} bytes`;
    elements.blockMeta.textContent = `${blockRangeLabel(draft.activeBlock)} (block ${draft.activeBlock})`;
    elements.selectedIndex.textContent = `${formatGlyphIndex(draft.selectedGlyph)} / ${draft.selectedGlyph}`;
    elements.selectedPosition.textContent = `Block ${draft.activeBlock}, slot ${formatHexBytePrefixed(glyphInBlock)}`;
    elements.glyphByteOutput.value = buildGlyphByteOutput(draft);
    elements.dirtyIndicator.textContent = draft.dirty ? "Dirty" : "Clean";
    elements.dirtyIndicator.classList.toggle("is-dirty", draft.dirty);
    elements.goToInput.disabled = false;
    elements.goToTrigger.disabled = false;
    elements.exportTrigger.disabled = false;
    elements.copySlotTrigger.disabled = false;
    elements.moveSlotTrigger.disabled = false;
    elements.swapSlotTrigger.disabled = false;
    elements.cancelSlotActionTrigger.disabled = atlasSlotAction === null;
    elements.undoSlotActionTrigger.disabled = atlasSlotUndo === null;
    elements.clearTrigger.disabled = false;
    elements.invertTrigger.disabled = false;
    elements.shiftLeftTrigger.disabled = false;
    elements.shiftRightTrigger.disabled = false;
    elements.shiftUpTrigger.disabled = false;
    elements.shiftDownTrigger.disabled = false;
    elements.copySlotTrigger.classList.toggle("is-active", atlasSlotAction?.kind === "copy");
    elements.moveSlotTrigger.classList.toggle("is-active", atlasSlotAction?.kind === "move");
    elements.swapSlotTrigger.classList.toggle("is-active", atlasSlotAction?.kind === "swap");
    if (atlasSlotAction === null) {
      const undoText =
        atlasSlotUndo === null
          ? ""
          : ` Undo is available for the last ${atlasSlotActionLabel(atlasSlotUndo.kind).toLowerCase()} action.`;
      elements.slotActionSummary.textContent = `Source: ${formatGlyphIndex(draft.selectedGlyph)}. Choose Copy, Move, or Swap, then click a target glyph in the atlas.${undoText}`;
      return;
    }

    elements.slotActionSummary.textContent = `${atlasSlotActionLabel(atlasSlotAction.kind)} mode armed from ${formatGlyphIndex(atlasSlotAction.sourceGlyph)}. Click a target glyph in the atlas; you can switch blocks first.`;
  };

  const renderSourceSummary = (target: ActiveCharsetTarget | null, errorText: string | null): void => {
    if (draft === null) {
      elements.sourceSummary.textContent =
        errorText === null && target !== null ? `No local draft yet. ${target.sourceLabel}.` : errorText ?? "No local draft yet.";
      return;
    }

    const liveText = errorText === null && target !== null ? target.sourceLabel : errorText ?? "Active live charset unavailable.";
    elements.sourceSummary.textContent = `Draft source: ${draft.sourceLabel}. ${liveText}.`;
  };

  const renderWriteControls = (target: ActiveCharsetTarget | null, errorText: string | null): void => {
    if (draft === null) {
      elements.writeGlyphTrigger.disabled = true;
      elements.writeBlockTrigger.disabled = true;
      elements.writeDraftTrigger.disabled = true;
      elements.writeHint.textContent = "Load or create a draft first.";
      return;
    }

    if (errorText !== null) {
      elements.writeGlyphTrigger.disabled = true;
      elements.writeBlockTrigger.disabled = true;
      elements.writeDraftTrigger.disabled = true;
      elements.writeHint.textContent = errorText;
      return;
    }

    if (target === null) {
      elements.writeGlyphTrigger.disabled = true;
      elements.writeBlockTrigger.disabled = true;
      elements.writeDraftTrigger.disabled = true;
      elements.writeHint.textContent = "Active live charset unavailable.";
      return;
    }

    if (target.backing === "rom") {
      elements.writeGlyphTrigger.disabled = true;
      elements.writeBlockTrigger.disabled = true;
      elements.writeDraftTrigger.disabled = true;
      elements.writeHint.textContent =
        "Active charset currently comes from character ROM; export or switch to a RAM-backed charset target.";
      return;
    }

    const targetBlocks = blockCount(target.glyphCount);
    const canWriteGlyph = draft.selectedGlyph < target.glyphCount;
    const canWriteBlock = draft.activeBlock < targetBlocks;
    const canWriteDraft = draft.glyphCount === target.glyphCount;

    elements.writeGlyphTrigger.disabled = !canWriteGlyph;
    elements.writeBlockTrigger.disabled = !canWriteBlock;
    elements.writeDraftTrigger.disabled = !canWriteDraft;

    if (!canWriteDraft) {
      elements.writeHint.textContent = `Active RAM charset is ${target.glyphCount} glyphs at ${formatHexWordPrefixed(target.baseAddress)}.`;
      return;
    }

    elements.writeHint.textContent = `Writes target the active ${target.glyphCount}-glyph RAM charset at ${formatHexWordPrefixed(target.baseAddress)} in video bank ${target.bank}.`;
  };

  const buildMenus = (): WorkbenchMenu[] => [
    {
      id: "file",
      label: "File",
      items: [
        buttonMenuItem("read-character-rom", elements.readRomTrigger),
        buttonMenuItem("read-active-charset", elements.readActiveTrigger),
        menuSeparator(),
        buttonMenuItem("new-blank", elements.createBlankTrigger),
        menuSeparator(),
        buttonMenuItem("import-binary", elements.importTrigger),
        buttonMenuItem("export-binary", elements.exportTrigger)
      ]
    },
    {
      id: "edit",
      label: "Edit",
      items: [
        buttonMenuItem("clear-glyph", elements.clearTrigger),
        buttonMenuItem("invert-glyph", elements.invertTrigger),
        menuSeparator(),
        buttonMenuItem("shift-left", elements.shiftLeftTrigger),
        buttonMenuItem("shift-right", elements.shiftRightTrigger),
        buttonMenuItem("shift-up", elements.shiftUpTrigger),
        buttonMenuItem("shift-down", elements.shiftDownTrigger)
      ]
    },
    {
      id: "view",
      label: "View",
      items: Array.from(elements.blockSelect.options).map((option) =>
        selectOptionMenuItem(`block-${option.value}`, elements.blockSelect, option.value)
      )
    },
    {
      id: "atlas",
      label: "Atlas",
      items: [
        buttonMenuItem("copy", elements.copySlotTrigger),
        buttonMenuItem("move", elements.moveSlotTrigger),
        buttonMenuItem("swap", elements.swapSlotTrigger),
        menuSeparator(),
        buttonMenuItem("undo-slot-action", elements.undoSlotActionTrigger),
        buttonMenuItem("cancel-slot-action", elements.cancelSlotActionTrigger)
      ]
    },
    {
      id: "write",
      label: "Write",
      items: [
        buttonMenuItem("write-glyph", elements.writeGlyphTrigger),
        buttonMenuItem("write-block", elements.writeBlockTrigger),
        buttonMenuItem("write-draft", elements.writeDraftTrigger)
      ]
    }
  ];

  const renderAll = (): void => {
    let activeTarget: ActiveCharsetTarget | null = null;
    let activeError: string | null = null;

    try {
      activeTarget = readActiveCharsetTarget(emulator);
    } catch (error) {
      activeError = error instanceof Error ? error.message : String(error);
    }

    renderBlockOptions();
    renderImportStartOptions();
    renderMetadata();
    renderSourceSummary(activeTarget, activeError);
    renderWriteControls(activeTarget, activeError);
    renderAtlas();
    renderEditor();
    refreshMenuBar();
  };

  const selectGlyph = (glyphIndex: number): void => {
    const currentDraft = draftOrThrow();
    if (glyphIndex < 0 || glyphIndex >= currentDraft.glyphCount) {
      throw new Error(`glyph index ${glyphIndex} is outside the current ${currentDraft.glyphCount}-glyph draft`);
    }
    currentDraft.selectedGlyph = glyphIndex;
    currentDraft.activeBlock = Math.floor(glyphIndex / GLYPHS_PER_BLOCK);
    renderAll();
  };

  const mutateSelectedGlyph = (mutation: (currentDraft: CharsetDraft, glyphIndex: number) => void, message: string): void => {
    const currentDraft = draftOrThrow();
    clearAtlasSlotUndo();
    mutation(currentDraft, currentDraft.selectedGlyph);
    currentDraft.dirty = true;
    setStatus(message);
    renderAll();
  };

  const replaceDraft = (nextDraft: CharsetDraft, message: string): void => {
    draft = nextDraft;
    clearAtlasSlotAction();
    clearAtlasSlotUndo();
    elements.goToInput.value = "";
    setStatus(message);
    renderAll();
  };

  const armAtlasSlotAction = (kind: AtlasSlotActionKind): void => {
    const currentDraft = draftOrThrow();
    atlasSlotAction = {
      kind,
      sourceGlyph: currentDraft.selectedGlyph
    };
    setStatus(`${atlasSlotActionLabel(kind)} mode armed from ${formatGlyphIndex(currentDraft.selectedGlyph)}. Click a target glyph in the atlas.`);
    renderAll();
  };

  const applyAtlasSlotAction = (targetGlyph: number): void => {
    const currentDraft = draftOrThrow();
    const action = atlasSlotAction;

    if (action === null) {
      currentDraft.selectedGlyph = targetGlyph;
      setStatus(`Selected glyph ${formatGlyphIndex(targetGlyph)}.`);
      renderAll();
      return;
    }

    if (targetGlyph === action.sourceGlyph) {
      setStatus(`${atlasSlotActionLabel(action.kind)} source ${formatGlyphIndex(action.sourceGlyph)} needs a different target glyph.`);
      renderAll();
      return;
    }

    if (action.kind === "copy") {
      atlasSlotUndo = {
        activeBlock: currentDraft.activeBlock,
        bytes: cloneBytes(currentDraft.bytes),
        dirty: currentDraft.dirty,
        kind: action.kind,
        selectedGlyph: currentDraft.selectedGlyph,
        sourceGlyph: action.sourceGlyph,
        targetGlyph,
      };
      copyGlyphSlot(currentDraft, action.sourceGlyph, targetGlyph);
    } else if (action.kind === "move") {
      atlasSlotUndo = {
        activeBlock: currentDraft.activeBlock,
        bytes: cloneBytes(currentDraft.bytes),
        dirty: currentDraft.dirty,
        kind: action.kind,
        selectedGlyph: currentDraft.selectedGlyph,
        sourceGlyph: action.sourceGlyph,
        targetGlyph,
      };
      moveGlyphSlot(currentDraft, action.sourceGlyph, targetGlyph);
    } else {
      atlasSlotUndo = {
        activeBlock: currentDraft.activeBlock,
        bytes: cloneBytes(currentDraft.bytes),
        dirty: currentDraft.dirty,
        kind: action.kind,
        selectedGlyph: currentDraft.selectedGlyph,
        sourceGlyph: action.sourceGlyph,
        targetGlyph,
      };
      swapGlyphSlots(currentDraft, action.sourceGlyph, targetGlyph);
    }

    currentDraft.selectedGlyph = targetGlyph;
    currentDraft.activeBlock = Math.floor(targetGlyph / GLYPHS_PER_BLOCK);
    currentDraft.dirty = true;
    clearAtlasSlotAction();
    if (action.kind === "copy") {
      setStatus(`Copied glyph ${formatGlyphIndex(action.sourceGlyph)} to ${formatGlyphIndex(targetGlyph)}.`);
    } else if (action.kind === "move") {
      setStatus(`Moved glyph ${formatGlyphIndex(action.sourceGlyph)} to ${formatGlyphIndex(targetGlyph)} and cleared the source.`);
    } else {
      setStatus(`Swapped glyph ${formatGlyphIndex(action.sourceGlyph)} with ${formatGlyphIndex(targetGlyph)}.`);
    }
    renderAll();
  };

  const undoAtlasSlotAction = (): void => {
    const currentDraft = draftOrThrow();
    const undo = atlasSlotUndo;
    if (undo === null) {
      throw new Error("No atlas slot action is available to undo.");
    }

    currentDraft.bytes = cloneBytes(undo.bytes);
    currentDraft.selectedGlyph = undo.selectedGlyph;
    currentDraft.activeBlock = undo.activeBlock;
    currentDraft.dirty = undo.dirty;
    clearAtlasSlotAction();
    clearAtlasSlotUndo();
    setStatus(
      `Undid ${atlasSlotActionLabel(undo.kind).toLowerCase()} from ${formatGlyphIndex(undo.sourceGlyph)} to ${formatGlyphIndex(undo.targetGlyph)}.`
    );
    renderAll();
  };

  const loadCharacterRom = (): void => {
    if (!confirmDiscardDirtyDraft("loading the character ROM")) {
      setStatus("Kept the current draft.");
      return;
    }

    const bytes = emulator.copyCharacterRom(0, CHARACTER_ROM_SIZE);
    replaceDraft(
      createDraft(512, bytes, "rom", "Character ROM"),
      "Loaded 512-glyph character ROM into the local draft."
    );
  };

  const loadActiveCharset = (): void => {
    if (!confirmDiscardDirtyDraft("loading the active charset")) {
      setStatus("Kept the current draft.");
      return;
    }

    const target = readActiveCharsetTarget(emulator);
    const byteLength = target.glyphCount * GLYPH_STRIDE;
    const bytes =
      target.backing === "rom"
        ? emulator.copyCharacterRom(target.charOffset << 10, byteLength)
        : emulator.copyMemory(target.baseAddress, byteLength);

    replaceDraft(
      createDraft(target.glyphCount, bytes, "active", target.sourceLabel),
      `Loaded active ${target.glyphCount}-glyph charset into the local draft.`
    );
  };

  const newBlankDraft = (): void => {
    if (!confirmDiscardDirtyDraft("creating a blank draft")) {
      setStatus("Kept the current draft.");
      return;
    }

    const glyphCount = Number.parseInt(elements.newBlankSize.value, 10);
    if (glyphCount !== 128 && glyphCount !== 256 && glyphCount !== 512) {
      throw new Error("blank draft size must be 128, 256, or 512 glyphs");
    }

    replaceDraft(
      createDraft(glyphCount, new Uint8Array(glyphCount * GLYPH_STRIDE), "blank", `Blank ${glyphCount}`),
      `Created a blank ${glyphCount}-glyph draft.`
    );
  };

  const exportDraft = (): void => {
    const currentDraft = draftOrThrow();
    downloadBinary(currentDraft.bytes, `charset-${currentDraft.glyphCount}.chr`);
    setStatus(`Exported ${currentDraft.bytes.length} bytes from the current draft.`);
  };

  const importFile = async (file: File): Promise<void> => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const glyphCount = glyphCountFromByteLength(bytes.length);
    if (glyphCount === null) {
      throw new Error("Character imports must be exactly 1024, 2048, or 4096 bytes.");
    }

    if (draft === null || glyphCount >= draft.glyphCount) {
      if (!confirmDiscardDirtyDraft(`importing ${file.name}`)) {
        setStatus("Kept the current draft.");
        return;
      }

      replaceDraft(
        createDraft(glyphCount, bytes, "file", file.name.trim() === "" ? `Imported ${glyphCount}` : file.name),
        `Imported ${bytes.length} bytes from ${file.name || "the selected file"}.`
      );
      return;
    }

    const currentDraft = draftOrThrow();
    const importBlocks = blockCount(glyphCount);
    const startBlock = Number.parseInt(elements.importStartBlock.value, 10);
    if (!Number.isFinite(startBlock) || !Number.isInteger(startBlock) || startBlock < 0) {
      throw new Error("Import start block must be a valid block number.");
    }
    if (startBlock + importBlocks > blockCount(currentDraft.glyphCount)) {
      throw new Error(
        `Imported ${glyphCount}-glyph file does not fit starting at block ${startBlock}; choose an earlier start block.`
      );
    }

    currentDraft.bytes.set(bytes, startBlock * BYTES_PER_BLOCK);
    currentDraft.sourceKind = "file";
    currentDraft.sourceLabel = `${file.name || `Imported ${glyphCount}`}`;
    currentDraft.selectedGlyph = startBlock * GLYPHS_PER_BLOCK;
    currentDraft.activeBlock = startBlock;
    currentDraft.dirty = true;
    clearAtlasSlotUndo();
    setStatus(`Imported ${glyphCount} glyphs into block ${startBlock}.`);
    renderAll();
  };

  const writeGlyph = (): void => {
    const currentDraft = draftOrThrow();
    const target = readActiveCharsetTarget(emulator);
    if (target.backing !== "ram") {
      throw new Error("Active charset currently comes from character ROM; RAM writeback is unavailable.");
    }
    if (currentDraft.selectedGlyph >= target.glyphCount) {
      throw new Error(`Selected glyph ${formatGlyphIndex(currentDraft.selectedGlyph)} is outside the active ${target.glyphCount}-glyph RAM charset.`);
    }

    const offset = glyphOffset(currentDraft.selectedGlyph);
    clearAtlasSlotUndo();
    emulator.writeMemory(target.baseAddress + offset, currentDraft.bytes.slice(offset, offset + GLYPH_STRIDE));
    setStatus(`Wrote glyph ${formatGlyphIndex(currentDraft.selectedGlyph)} to active RAM charset at ${formatHexWordPrefixed(target.baseAddress + offset)}.`);
    renderAll();
  };

  const writeBlock = (): void => {
    const currentDraft = draftOrThrow();
    const target = readActiveCharsetTarget(emulator);
    if (target.backing !== "ram") {
      throw new Error("Active charset currently comes from character ROM; RAM writeback is unavailable.");
    }

    const targetBlocks = blockCount(target.glyphCount);
    if (currentDraft.activeBlock >= targetBlocks) {
      throw new Error(`Block ${currentDraft.activeBlock} is outside the active ${target.glyphCount}-glyph RAM charset.`);
    }

    const offset = currentDraft.activeBlock * BYTES_PER_BLOCK;
    clearAtlasSlotUndo();
    emulator.writeMemory(target.baseAddress + offset, currentDraft.bytes.slice(offset, offset + BYTES_PER_BLOCK));
    setStatus(`Wrote block ${currentDraft.activeBlock} to active RAM charset at ${formatHexWordPrefixed(target.baseAddress + offset)}.`);
    renderAll();
  };

  const writeDraft = (): void => {
    const currentDraft = draftOrThrow();
    const target = readActiveCharsetTarget(emulator);
    if (target.backing !== "ram") {
      throw new Error("Active charset currently comes from character ROM; RAM writeback is unavailable.");
    }
    if (currentDraft.glyphCount !== target.glyphCount) {
      throw new Error(`Full draft write requires a ${target.glyphCount}-glyph draft to match the active RAM charset.`);
    }

    clearAtlasSlotUndo();
    emulator.writeMemory(target.baseAddress, currentDraft.bytes);
    currentDraft.dirty = false;
    setStatus(`Wrote ${currentDraft.glyphCount}-glyph draft to active RAM charset at ${formatHexWordPrefixed(target.baseAddress)}.`);
    renderAll();
  };

  elements.readRomTrigger.addEventListener("click", () => {
    try {
      loadCharacterRom();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.readActiveTrigger.addEventListener("click", () => {
    try {
      loadActiveCharset();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.createBlankTrigger.addEventListener("click", () => {
    try {
      newBlankDraft();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.importTrigger.addEventListener("click", () => {
    elements.importFileInput.click();
  });

  elements.importFileInput.addEventListener("change", () => {
    const [file] = Array.from(elements.importFileInput.files ?? []);
    if (file === undefined) {
      return;
    }

    void importFile(file)
      .catch((error: unknown) => {
        setStatus(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        elements.importFileInput.value = "";
        renderAll();
      });
  });

  elements.exportTrigger.addEventListener("click", () => {
    try {
      exportDraft();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.copySlotTrigger.addEventListener("click", () => {
    try {
      armAtlasSlotAction("copy");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.moveSlotTrigger.addEventListener("click", () => {
    try {
      armAtlasSlotAction("move");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.swapSlotTrigger.addEventListener("click", () => {
    try {
      armAtlasSlotAction("swap");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.cancelSlotActionTrigger.addEventListener("click", () => {
    clearAtlasSlotAction();
    if (draft === null) {
      setStatus("Open the Characters tab or load a draft to inspect and edit character glyphs.");
    } else {
      setStatus(`Cancelled atlas slot action for ${formatGlyphIndex(draft.selectedGlyph)}.`);
    }
    renderAll();
  });

  elements.undoSlotActionTrigger.addEventListener("click", () => {
    try {
      undoAtlasSlotAction();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.blockSelect.addEventListener("change", () => {
    if (draft === null) {
      return;
    }

    const nextBlock = Number.parseInt(elements.blockSelect.value, 10);
    if (!Number.isFinite(nextBlock) || !Number.isInteger(nextBlock)) {
      return;
    }

    draft.activeBlock = Math.max(0, Math.min(nextBlock, blockCount(draft.glyphCount) - 1));
    const blockStart = draft.activeBlock * GLYPHS_PER_BLOCK;
    const blockEnd = blockStart + GLYPHS_PER_BLOCK - 1;
    if (draft.selectedGlyph < blockStart || draft.selectedGlyph > blockEnd) {
      draft.selectedGlyph = blockStart;
    }
    renderAll();
  });

  elements.goToTrigger.addEventListener("click", () => {
    try {
      const currentDraft = draftOrThrow();
      const glyphIndex = parseCharsetIndex(elements.goToInput.value);
      if (glyphIndex >= currentDraft.glyphCount) {
        throw new Error(`glyph index ${glyphIndex} is outside the current ${currentDraft.glyphCount}-glyph draft`);
      }
      selectGlyph(glyphIndex);
      setStatus(`Jumped to glyph ${formatGlyphIndex(glyphIndex)}.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.atlasCanvas.addEventListener("click", (event) => {
    if (draft === null) {
      return;
    }

    const point = canvasPoint(event, elements.atlasCanvas);
    const col = Math.floor(point.x / (elements.atlasCanvas.getBoundingClientRect().width / ATLAS_COLUMNS));
    const row = Math.floor(point.y / (elements.atlasCanvas.getBoundingClientRect().height / ATLAS_ROWS));
    if (col < 0 || col >= ATLAS_COLUMNS || row < 0 || row >= ATLAS_ROWS) {
      return;
    }

    const glyphIndex = draft.activeBlock * GLYPHS_PER_BLOCK + row * ATLAS_COLUMNS + col;
    if (glyphIndex >= draft.glyphCount) {
      return;
    }
    applyAtlasSlotAction(glyphIndex);
  });

  elements.glyphEditorCanvas.addEventListener("click", (event) => {
    if (draft === null) {
      return;
    }

    const point = canvasPoint(event, elements.glyphEditorCanvas);
    const col = Math.floor(point.x / (elements.glyphEditorCanvas.getBoundingClientRect().width / GLYPH_DIMENSION));
    const row = Math.floor(point.y / (elements.glyphEditorCanvas.getBoundingClientRect().height / GLYPH_DIMENSION));
    if (col < 0 || col >= GLYPH_DIMENSION || row < 0 || row >= GLYPH_DIMENSION) {
      return;
    }

    const nextPixel = !getGlyphPixel(draft, draft.selectedGlyph, row, col);
    clearAtlasSlotUndo();
    setGlyphPixel(draft, draft.selectedGlyph, row, col, nextPixel);
    draft.dirty = true;
    setStatus(`Edited glyph ${formatGlyphIndex(draft.selectedGlyph)} locally.`);
    renderAll();
  });

  elements.clearTrigger.addEventListener("click", () => {
    try {
      mutateSelectedGlyph(clearGlyph, `Cleared glyph ${formatGlyphIndex(draftOrThrow().selectedGlyph)}.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.invertTrigger.addEventListener("click", () => {
    try {
      mutateSelectedGlyph(invertGlyph, `Inverted glyph ${formatGlyphIndex(draftOrThrow().selectedGlyph)}.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.shiftLeftTrigger.addEventListener("click", () => {
    try {
      mutateSelectedGlyph(shiftGlyphLeft, `Shifted glyph ${formatGlyphIndex(draftOrThrow().selectedGlyph)} left.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.shiftRightTrigger.addEventListener("click", () => {
    try {
      mutateSelectedGlyph(shiftGlyphRight, `Shifted glyph ${formatGlyphIndex(draftOrThrow().selectedGlyph)} right.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.shiftUpTrigger.addEventListener("click", () => {
    try {
      mutateSelectedGlyph(shiftGlyphUp, `Shifted glyph ${formatGlyphIndex(draftOrThrow().selectedGlyph)} up.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.shiftDownTrigger.addEventListener("click", () => {
    try {
      mutateSelectedGlyph(shiftGlyphDown, `Shifted glyph ${formatGlyphIndex(draftOrThrow().selectedGlyph)} down.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
    }
  });

  elements.writeGlyphTrigger.addEventListener("click", () => {
    try {
      writeGlyph();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.writeBlockTrigger.addEventListener("click", () => {
    try {
      writeBlock();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  elements.writeDraftTrigger.addEventListener("click", () => {
    try {
      writeDraft();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error));
      renderAll();
    }
  });

  menuBar = createWorkbenchMenuBar(menuHost, {
    getMenus: buildMenus,
    label: "Character Editor menu bar"
  });

  setStatus("Open the Characters tab or load a draft to inspect and edit character glyphs.");
  renderAll();

  return {
    exportDraftForTileset() {
      if (draft === null) {
        return null;
      }
      return {
        activeBlock: draft.activeBlock,
        bytes: cloneBytes(draft.bytes),
        glyphCount: draft.glyphCount,
        sourceLabel: draft.sourceLabel
      };
    },
    importExternalDraft(nextDraft) {
      if (nextDraft.glyphCount !== 128 && nextDraft.glyphCount !== 256 && nextDraft.glyphCount !== 512) {
        throw new Error("External character draft must contain 128, 256, or 512 glyphs.");
      }
      if (nextDraft.bytes.length !== nextDraft.glyphCount * GLYPH_STRIDE) {
        throw new Error(
          `External character draft byte length ${nextDraft.bytes.length} does not match ${nextDraft.glyphCount} glyphs.`
        );
      }
      if (!confirmDiscardDirtyDraft("loading an external tileset draft")) {
        setStatus("Kept the current draft.");
        renderAll();
        return false;
      }

      const imported = createDraft(nextDraft.glyphCount, nextDraft.bytes, "handoff", nextDraft.sourceLabel);
      const maxBlock = blockCount(imported.glyphCount) - 1;
      imported.activeBlock = Math.max(0, Math.min(nextDraft.activeBlock, maxBlock));
      imported.selectedGlyph = imported.activeBlock * GLYPHS_PER_BLOCK;
      replaceDraft(imported, `Loaded ${nextDraft.glyphCount}-glyph draft from Tile Map.`);
      return true;
    },
    refresh() {
      renderAll();
    }
  };
}
